import React, {
  cloneElement,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import {
  addOverlay,
  removeOverlay,
  updateOverlay,
} from '../../Actions/Overlay.action';
import Icon from '../Icon';
import {
  StyledSideMenu,
  StyledSideMenuCloseButton,
  StyledSideMenuContent,
  StyledSideMenuFooter,
  StyledSideMenuHeader,
} from './styles/SideMenu.styling';
import { createGlobalStyle } from 'styled-components';

interface SideMenuProps {
  id: string;
  duration?: string;
  from?: string;
  className?: string;
  open?: boolean;
  onClosed: () => void;
  showCloseButton?: boolean;
  children?: React.ReactNode;
  title?: string;
  disableClosing?: boolean;
  closeButton?: any;
  components?: {
    Header?: React.FC;
    Footer?: React.FC;
    Content?: React.FC;
  };
}


const GlobalSideMenuStyling = createGlobalStyle`
  body #header {
    z-index: 50;
  }
`;


export default forwardRef<HTMLDivElement, SideMenuProps>(
  (
    {
      id,
      duration = '0.2s',
      from = 'right',
      className,
      open = false,
      onClosed,
      showCloseButton = true,
      children,
      title,
      disableClosing = false,
      closeButton = null,
      components = {},
    },
    ref
  ) => {
    const [ready, setReady] = useState<boolean>(false);
    const [slideIn, setSlideIn] = useState<boolean>(open);
    const [renderSideMenu, setRenderSideMenu] = useState<boolean>(open);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();
    const [modalId, setModalId] = useState('');
    const dispatch = useDispatch();
    const focusableElements =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function setFirstFocus() {
      if (modalId == null) {
        return;
      }
      let modalElement = document.getElementById(modalId);
      let firstFocusableElement = modalElement?.querySelectorAll(
        focusableElements
      )[0];

      if (firstFocusableElement != undefined) {
        (firstFocusableElement as HTMLElement)?.focus();
      }
    }

    if (!id || id == '') {
      throw new Error(
        'A unique side-menu "id" must be passed to side menu component'
      );
    }

    useEffect(() => {
      setReady(true);
    }, []);

    const close = useCallback(() => {
      if (!disableClosing) {
        // Just sets "slideIn" to false, which triggers the animation to close it
        setSlideIn(false);
        return true;
      }

      // False disables overlay click
      return false;
    }, [disableClosing]);

    useEffect(() => {
      dispatch(updateOverlay(id, close));
    }, [dispatch, close, id]);

    useEffect(() => {
      if (!ready) return;
      if (!slideIn && ready) {
        // If not slide in, slide out :^)
        if (timeoutId) {
          // Clear previous timeout to avoid animation delay bug
          clearTimeout(timeoutId);
        }

        var newTimeoutId = setTimeout(() => {
          setTimeoutId(undefined);
          onClosed && onClosed();
          setRenderSideMenu(false);
        }, parseFloat(duration) * 1000);

        setTimeoutId(newTimeoutId);
        dispatch(removeOverlay(id));
      }
    }, [slideIn]);

    useEffect(() => {
      if (!ready) return;

      // Triggered every time "open" is changed, initially as well
      // Timeout to not trigger on the same frame as the render, to enable the slide-in
      setTimeout(() => setSlideIn(open), 1);
      setModalId('modal-' + id);
      if (open) {
        setRenderSideMenu(true);
        dispatch(addOverlay(id, close));
      }
    }, [open, close]);

    useEffect(() => {
      if (modalId != undefined) {
        setTimeout(() => setFirstFocus(), 1);

      }
    }, [modalId, open]);

    const CloseIcon = () => (
      <StyledSideMenuCloseButton name="close" onClick={close}>
        <Icon name="close" />
      </StyledSideMenuCloseButton>
    );

    return renderSideMenu
      ? ReactDOM.createPortal(
        <StyledSideMenu
          $open={slideIn}
          $from={from}
          className={`${slideIn ? 'open' : 'closing'} ${className ?? ''.trim()
            }`}
          ref={ref}
          style={{ transitionDuration: duration }}
          id={'modal-' + id}
        >
          {components.Header && (
            <StyledSideMenuHeader className="sidemenu-header__wrapper">
              {showCloseButton &&
                !disableClosing &&
                (!closeButton ? (
                  <CloseIcon />
                ) : (
                  cloneElement(closeButton, {
                    onClick: close,
                  })
                ))}
              <components.Header />
            </StyledSideMenuHeader>
          )}
          {!components.Header &&
            showCloseButton &&
            !disableClosing &&
            (!closeButton ? (
              <CloseIcon />
            ) : (
              cloneElement(closeButton, {
                onClick: close,
              })
            ))}
          {title && <h5 className="title">{title}</h5>}
          {components?.Content && (
            <components.Content>{children}</components.Content>
          )}
          {!components?.Content && (
            <StyledSideMenuContent className="side-menu-content">
              {children}
            </StyledSideMenuContent>
          )}
          {components.Footer != null && (
            <StyledSideMenuFooter>
              <components.Footer />
            </StyledSideMenuFooter>
          )}
          <GlobalSideMenuStyling />
        </StyledSideMenu>,
        document.body
      )
      : null;
  }
);
